import { normalizeCompletionPolicy } from '../../completionPolicy';
import {
    DEFAULT_PHYSICAL_SCALE,
    normalizeRulerPhysicalScaleConfig,
    validatePhysicalScaleConfig,
} from '../RulerMeasurementUI/rulerMeasurementModel';
import { RULE_TYPES } from './validation';

export { DEFAULT_PHYSICAL_SCALE };

const RULE_TYPE_IDS = new Set(RULE_TYPES.map((r) => r.id));
const UNIT_IDS = new Set(['px', 'µm', 'auto']);

function toTrimmedString(v) {
    return typeof v === 'string' ? v.trim() : '';
}

/** Allowed single-key hotkeys: digits and Latin letters (stored lowercase). */
const HOTKEY_CHAR = /^[0-9a-z]$/i;

/** Letters reserved for bbox UI mode shortcuts (not valid category hotkeys). */
const UI_MODE_RESERVED_CATEGORY_LETTERS = new Set(['d', 'm', 'r']);

/**
 * @param {string} ch single lowercase letter
 */
export function isUiReservedCategoryHotkeyKey(ch) {
    return typeof ch === 'string' && UI_MODE_RESERVED_CATEGORY_LETTERS.has(ch.toLowerCase());
}

/**
 * User-facing reason when a letter is reserved for mode switching.
 * @param {'d'|'m'|'r'} letterLower
 */
export function getUiReservedCategoryHotkeyMessage(letterLower) {
    const L = letterLower.toLowerCase();
    if (L === 'd') {
        return 'This key is reserved for Draw mode (press D in the viewer). Choose another digit or letter.';
    }
    if (L === 'm') {
        return 'This key is reserved for Modify mode (press M in the viewer). Choose another digit or letter.';
    }
    if (L === 'r') {
        return 'This key is reserved for Review mode (press R in the viewer). Choose another digit or letter.';
    }
    return 'This key is reserved for a viewer shortcut. Choose another digit or letter.';
}

/** Long help for project settings Tooltip (category hotkeys). */
export function getCategoryHotkeyConfigHelpText() {
    return [
        'Optional: one digit (0–9) or letter (a–z) per category. In the viewer, use it in Draw to pick the draw class, and in Modify or Review to reclassify the selection (or current review item).',
        'Reserved for the viewer (not available as category hotkeys): D / M / R switch Draw / Modify / Review; Esc returns to View; Space activates click-to-select in Modify and Review; Tab / Shift+Tab step to the next or previous box in Review.',
        'Duplicate category hotkeys are flagged at publish time.',
    ].join(' ');
}

/** Inline config UI when the user types a non-digit/non-letter hotkey (symbols, etc.). */
export const CATEGORY_HOTKEY_INVALID_CHARACTER_MESSAGE =
    'Use a single digit (0–9) or letter (a–z). Letters D, M, and R are reserved for the viewer.';

/**
 * Normalize optional category hotkey for config + keyboard matching.
 * @param {unknown} raw
 * @returns {string|null}
 */
export function normalizeCategoryHotkey(raw) {
    const s = toTrimmedString(raw);
    if (!s || s.length !== 1 || !HOTKEY_CHAR.test(s)) return null;
    if (/[0-9]/.test(s)) return s;
    const lower = s.toLowerCase();
    if (UI_MODE_RESERVED_CATEGORY_LETTERS.has(lower)) return null;
    return lower;
}

export const DEFAULT_BBOX_CONFIG = {
    instructions: '',
    categories: [],
    physicalScale: DEFAULT_PHYSICAL_SCALE,
};

export function nextBoundingBoxCategoryId(categories) {
    const used = new Set(
        (Array.isArray(categories) ? categories : [])
            .map((c) => toTrimmedString(c?.id))
            .filter(Boolean)
    );
    let n = 1;
    while (used.has(`category-${n}`)) n += 1;
    return `category-${n}`;
}

export function createDraftBoundingBoxCategory(categories) {
    return {
        id: nextBoundingBoxCategoryId(categories),
        label: '',
    };
}

function normalizeRule(r) {
    const type = toTrimmedString(r?.type);
    if (!RULE_TYPE_IDS.has(type)) return null;
    const v = Number(r?.value);
    if (!Number.isFinite(v)) return null;
    const unit = toTrimmedString(r?.unit);
    const u = UNIT_IDS.has(unit) ? unit : 'px';
    return { type, value: v, unit: u };
}

function normalizeOneCategory(c, index, seenIds) {
    const preferredId = toTrimmedString(c?.id);
    const fallbackId = `category-${index + 1}`;
    let id = preferredId || fallbackId;
    if (seenIds.has(id)) {
        let k = 2;
        while (seenIds.has(`${id}-${k}`)) k += 1;
        id = `${id}-${k}`;
    }
    seenIds.add(id);
    const label = toTrimmedString(c?.label) || id;
    const rulesRaw = Array.isArray(c?.rules) ? c.rules : [];
    const rules = rulesRaw.map(normalizeRule).filter(Boolean);
    const hotkey = normalizeCategoryHotkey(c?.hotkey);
    const out = {
        id,
        label,
        style: c?.style && typeof c.style === 'object' ? c.style : {},
        rules,
    };
    if (hotkey) out.hotkey = hotkey;
    return out;
}

export function normalizeBoundingBoxCategories(rawCategories) {
    const list = Array.isArray(rawCategories) ? rawCategories : [];
    const seen = new Set();
    return list.map((c, i) => normalizeOneCategory(c, i, seen));
}

/**
 * Canonical bounding-box annotation UI config (safe for runtime + publish checks).
 * @param {object} [config]
 */
export function normalizeBoundingBoxConfig(config) {
    const raw = config && typeof config === 'object' ? config : {};
    const categories = normalizeBoundingBoxCategories(raw.categories);
    const out = {
        ...DEFAULT_BBOX_CONFIG,
        ...raw,
        instructions: typeof raw.instructions === 'string' ? raw.instructions : '',
        categories,
        physicalScale: normalizeRulerPhysicalScaleConfig(raw.physicalScale),
        completionPolicy: normalizeCompletionPolicy(raw),
    };
    if (raw.roi && typeof raw.roi === 'object') {
        out.roi = raw.roi;
    }
    return out;
}

/**
 * @returns {{code: string, message: string}[]}
 */
export function validateBoundingBoxConfig(config) {
    const issues = [];
    const raw = config && typeof config === 'object' ? config : {};
    const cats = Array.isArray(raw.categories) ? raw.categories : [];

    if (cats.length === 0) {
        issues.push({
            code: 'missing_categories',
            message: 'Add at least one bounding box category in Annotation UI configuration.',
        });
    }

    const seen = new Set();
    const seenHotkeys = new Set();
    cats.forEach((c, idx) => {
        const id = toTrimmedString(c?.id);
        const label = toTrimmedString(c?.label);
        const row = idx + 1;
        if (!id) {
            issues.push({
                code: 'empty_category_id',
                message: `Bounding box category ${row} is missing an id.`,
            });
        } else if (seen.has(id)) {
            issues.push({
                code: 'duplicate_category_id',
                message: `Bounding box category id "${id}" is duplicated.`,
            });
        } else {
            seen.add(id);
        }
        if (id && !label) {
            issues.push({
                code: 'missing_category_label',
                message: `Bounding box category "${id}" is missing a label.`,
            });
        }

        const hotkeyRaw = toTrimmedString(c?.hotkey);
        if (hotkeyRaw) {
            if (hotkeyRaw.length === 1 && HOTKEY_CHAR.test(hotkeyRaw) && isUiReservedCategoryHotkeyKey(hotkeyRaw)) {
                const letter = hotkeyRaw.toLowerCase();
                issues.push({
                    code: 'reserved_category_hotkey',
                    message: id
                        ? `Category "${id}": ${getUiReservedCategoryHotkeyMessage(letter)}`
                        : `Bounding box category ${row}: ${getUiReservedCategoryHotkeyMessage(letter)}`,
                });
            } else {
                const hk = normalizeCategoryHotkey(c?.hotkey);
                if (!hk) {
                    issues.push({
                        code: 'invalid_category_hotkey',
                        message: id
                            ? `Category "${id}": hotkey must be a single digit or letter (a–z), and not D, M, or R (reserved for modes).`
                            : `Bounding box category ${row}: hotkey must be a single digit or letter (a–z), and not D, M, or R (reserved for modes).`,
                    });
                } else if (seenHotkeys.has(hk)) {
                    issues.push({
                        code: 'duplicate_category_hotkey',
                        message: id
                            ? `Category "${id}": hotkey "${hk}" is already assigned to another category.`
                            : `Bounding box category ${row}: hotkey "${hk}" is duplicated.`,
                    });
                } else {
                    seenHotkeys.add(hk);
                }
            }
        }

        const rules = Array.isArray(c?.rules) ? c.rules : [];
        rules.forEach((r, ri) => {
            const prefix = id ? `Category "${id}"` : `Category row ${row}`;
            const type = toTrimmedString(r?.type);
            if (!type) {
                issues.push({
                    code: 'empty_rule_type',
                    message: `${prefix}: rule ${ri + 1} is missing a type.`,
                });
                return;
            }
            if (!RULE_TYPE_IDS.has(type)) {
                issues.push({
                    code: 'invalid_rule_type',
                    message: `${prefix}: rule type "${type}" is invalid.`,
                });
            }
            const v = r?.value;
            if (v == null || (typeof v === 'number' && !Number.isFinite(v))) {
                issues.push({
                    code: 'invalid_rule_value',
                    message: `${prefix}: rule "${type}" has an invalid numeric value.`,
                });
            }
            const unit = toTrimmedString(r?.unit);
            if (unit && !UNIT_IDS.has(unit)) {
                issues.push({
                    code: 'invalid_rule_unit',
                    message: `${prefix}: rule "${type}" has invalid unit "${unit}".`,
                });
            }
        });
    });

    issues.push(...validatePhysicalScaleConfig(raw.physicalScale));
    return issues;
}
